import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Form, Input, Button, Card, Typography, Alert, Checkbox, Result } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, HeartOutlined } from '@ant-design/icons';
import { useStores } from '../../stores';
import PhoneInput, { validatePhoneNumber } from '../../components/PhoneInput';
import i18n from '../../i18n';
import './AuthPages.scss';

const { Title, Text } = Typography;

const RegisterPage = observer(() => {
  const { authStore } = useStores();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const onFinish = async (values) => {
    const userData = {
      email: values.email,
      password: values.password,
      password2: values.password2,
      first_name: values.first_name,
      last_name: values.last_name,
      phone_number: values.phone_number || '',
      preferred_language: i18n.language || 'el',
    };

    const result = await authStore.register(userData);
    if (result.success) {
      setRegisteredEmail(values.email);
      setRegistrationSuccess(true);
    }
  };

  // Success state - show verification message
  if (registrationSuccess) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card-wrapper bounce-in">
            <Card className="auth-card">
              <Result
                status="success"
                title={t('auth.register.successTitle')}
                subTitle={
                  <div>
                    <p>{t('auth.register.successSubtitle')}</p>
                    <p><strong>{registeredEmail}</strong></p>
                    <p style={{ marginTop: 16 }}>
                      {t('auth.register.successMessage')}
                    </p>
                  </div>
                }
                extra={[
                  <Link to="/login" key="login">
                    <Button type="primary" size="large">
                      {t('auth.register.goToLogin')}
                    </Button>
                  </Link>
                ]}
              />
            </Card>

            <div className="auth-decoration">
              <div className="paw-print">🐾</div>
              <div className="paw-print">🐾</div>
              <div className="paw-print">🐾</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card-wrapper bounce-in">
          <Card className="auth-card">
            <div className="auth-header">
              <div className="logo-section">
                <HeartOutlined className="logo-icon" />
                <Title level={2} className="auth-title">
                  {t('auth.register.title')}
                </Title>
                <Text className="auth-subtitle">
                  {t('auth.register.subtitle')}
                </Text>
              </div>
            </div>

            {authStore.error && (
              <Alert
                message={authStore.error}
                type="error"
                showIcon
                closable
                onClose={() => authStore.clearError()}
                style={{ marginBottom: 24 }}
              />
            )}

            <Form
              form={form}
              name="register"
              onFinish={onFinish}
              layout="vertical"
              size="large"
              autoComplete="off"
            >
              <Form.Item
                name="email"
                label={t('auth.login.emailLabel')}
                rules={[
                  { required: true, message: t('auth.login.emailRequired') },
                  { type: 'email', message: t('auth.login.emailInvalid') },
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder={t('auth.login.emailPlaceholder')}
                  autoComplete="email"
                />
              </Form.Item>

              <div style={{ display: 'flex', gap: '12px' }}>
                <Form.Item
                  name="first_name"
                  label={t('auth.register.firstName')}
                  rules={[{ required: true, message: t('common.required') }]}
                  style={{ flex: 1 }}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder={t('auth.register.firstNamePlaceholder')}
                    autoComplete="given-name"
                  />
                </Form.Item>

                <Form.Item
                  name="last_name"
                  label={t('auth.register.lastName')}
                  rules={[{ required: true, message: t('common.required') }]}
                  style={{ flex: 1 }}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder={t('auth.register.lastNamePlaceholder')}
                    autoComplete="family-name"
                  />
                </Form.Item>
              </div>

              <Form.Item
                name="phone_number"
                label={t('common.phone')}
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve(); // Optional field
                      if (validatePhoneNumber(value)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error(t('auth.register.phoneInvalid')));
                    },
                  },
                ]}
              >
                <PhoneInput
                  placeholder={t('auth.register.phonePlaceholder')}
                />
              </Form.Item>

              <Form.Item
                name="password"
                label={t('auth.login.passwordLabel')}
                rules={[
                  { required: true, message: t('auth.login.passwordRequired') },
                  { min: 8, message: t('auth.register.passwordMinLength') },
                  {
                    pattern: /[A-Z]/,
                    message: t('auth.register.passwordUppercase'),
                  },
                  {
                    pattern: /[a-z]/,
                    message: t('auth.register.passwordLowercase'),
                  },
                  {
                    pattern: /[0-9]/,
                    message: t('auth.register.passwordNumber'),
                  },
                  {
                    pattern: /[!@#$%^&*(),.?":{}|<>]/,
                    message: t('auth.register.passwordSpecial'),
                  },
                ]}
                help={t('auth.register.passwordHelp')}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder={t('auth.register.passwordPlaceholder')}
                  autoComplete="new-password"
                />
              </Form.Item>

              <Form.Item
                name="password2"
                label={t('auth.register.confirmPassword')}
                dependencies={['password']}
                rules={[
                  { required: true, message: t('auth.register.confirmPasswordRequired') },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error(t('auth.register.passwordsDoNotMatch')));
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder={t('auth.register.confirmPasswordPlaceholder')}
                  autoComplete="new-password"
                />
              </Form.Item>

              <Form.Item
                name="terms"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) =>
                      value ? Promise.resolve() : Promise.reject(new Error(t('auth.register.termsAgreement'))),
                  },
                ]}
              >
                <Checkbox>
                  {t('auth.register.termsAgreement')}
                </Checkbox>
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={authStore.isLoading}
                  block
                  className="auth-button"
                >
                  {t('auth.register.submitButton')}
                </Button>
              </Form.Item>

              <div className="auth-footer">
                <Text>{t('auth.register.haveAccount')} </Text>
                <Link to="/login" className="auth-link">
                  {t('auth.register.signIn')}
                </Link>
              </div>
            </Form>
          </Card>

          <div className="auth-decoration">
            <div className="paw-print">🐾</div>
            <div className="paw-print">🐾</div>
            <div className="paw-print">🐾</div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default RegisterPage;
